import React from 'react';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from '../../../store';
import { DataElementContext } from '../../../page-components/common/DataElementContext';
import { requestLimits, setNewLimit } from '../../../modules/casino/store/actions/profile';

import './index.scss';
import { useMediaQuery } from '../../utils/useQueryMedia';
import { getFormattedCurrencyAmount } from '@/page-components/utils/functions';

type LimitsProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
  };
};

type Limit = {
  id: number | null;
  type: number | null;
  subtype: number | null | string;
  period_type: number | null;
  limit_amount: number | null;
  accumulated_amount: number | null;
  limit_start: number | null;
  limit_end: number | null;
  new_limit_amount: number | null;
};

const noDataLimit: Limit = {
  id: null,
  type: 1,
  subtype: null,
  period_type: 1,
  limit_amount: null,
  accumulated_amount: null,
  limit_start: null,
  limit_end: null,
  new_limit_amount: null,
};

type ModuleStateProps = {
  limitValue: number;
  limitType: number;
  hasLimitDaily: boolean;
  limitValueDaily: string;
  hasLimitWeekly: boolean;
  limitValueWeekly: string;
  hasLimitMonthly: boolean;
  limitValueMonthly: string;
  limitError: string | boolean;
  isConfirmationDialogOpen: boolean;
  currentStep: string;
  limitDaily?: { data: Limit };
  limitWeekly?: { data: Limit };
  limitMonthly?: { data: Limit };
};

interface Hash {
  [key: string]: string;
}
interface HashLimits {
  [key: string]: any;
}

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    dsType: '',
  },
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const Limits = (componentProps: LimitsProps) => {
  const tmpProps = { ...defaultProps, ...componentProps };
  delete tmpProps.children;
  const props = JSON.parse(JSON.stringify(tmpProps));
  const { children } = componentProps;

  const dispatch = useAppDispatch();
  const profile = useAppSelector((state) => state.profile);
  const currency = useAppSelector((state) => state.currencies.currentCurrency);
  const marketing_agreements = useAppSelector((state) => state.alerts.settings.marketing_agreements);

  const isDesktop = useMediaQuery('(min-width:900px)');

  const ERRORS = {
    MINIMUM_LIMIT: 'DEPOSIT_AMOUNT_ERROR_MINIMUM_LIMIT',
    MAXIMUM_LIMIT: 'DEPOSIT_AMOUNT_ERROR_MAXIMUM_LIMIT',
  };

  const PERIOD_TYPE_DAILY = 1;
  const PERIOD_TYPE_WEEKLY = 2;
  const PERIOD_TYPE_MONTHLY = 3;
  const PERIOD_TYPE_KYC = 'KYC';

  const limitTypes = {
    daily: PERIOD_TYPE_DAILY,
    weekly: PERIOD_TYPE_WEEKLY,
    monthly: PERIOD_TYPE_MONTHLY,
    KYC: PERIOD_TYPE_KYC,
  };

  const initialState = {
    hasLimitDaily: false,
    limitValueDaily: '',
    hasLimitWeekly: false,
    limitValueWeekly: '',
    hasLimitMonthly: false,
    limitValueMonthly: '',
    currentStep: 'default',
    limitType: 0,
    limitValue: window.config?.limits?.defaultAmount ?? 20,
    limitError: false,
    isConfirmationDialogOpen: false,
  };

  const [state, setState] = React.useState<ModuleStateProps>(initialState);

  React.useEffect(() => {
    dispatch(requestLimits());
  }, []);

  const getLimitText = (limit: Limit) => {
    let text = '';

    if (limit.new_limit_amount || limit.limit_end) {
      const limitAmount = limit.limit_amount ?? 0;
      const newLimitAmount = limit.new_limit_amount ?? 0;
      const limitEnd = limit.limit_end ?? 0;

      if ((limitAmount === 0 && newLimitAmount > 0) || (limitAmount > newLimitAmount && newLimitAmount !== 0)) {
        // text = 'Your new limit is now set to {{NEWLIMITAMOUNT}} {{CURRENCY}}';
        text = 'LIMITS.NEW_LIMIT_SET_PLACEHOLDER-{{NEWLIMITAMOUNT}}-{{CURRENCY}}';
      } else if (limitAmount < newLimitAmount) {
        // text = 'Your limit of {{NEWLIMITAMOUNT}} {{CURRENCY}} will be applied after 48h.';
        text = 'LIMITS.NEW_LIMIT_APPLIED_PLACEHOLDER-{{NEWLIMITAMOUNT}}-{{CURRENCY}}';
      } else if (limitAmount === newLimitAmount) {
        // text = 'Your limit already is {{NEWLIMITAMOUNT}} {{CURRENCY}}';
        text = 'LIMITS.NEW_LIMIT_ALREADY_SET_PLACEHOLDER-{{NEWLIMITAMOUNT}}-{{CURRENCY}}';
      } else if ((newLimitAmount === 0 && limitEnd !== 0) || (newLimitAmount && limitEnd)) {
        // text = 'Your limit will be removed after 48h.';
        text = 'LIMITS.REMOVE_LIMIT_PLACEHOLDER';
      }
    }

    return text;
  };

  const getLimit = (periodType: number | string | null) => {
    if (periodType === null) {
      return { ...noDataLimit, subtype: null, description: 'LIMITS.NO_LIMIT_SET_PLACEHOLDER' };
    }

    if (profile && profile.limits && profile.limits[periodType] != null) {
      // return profile.limits[periodType];
      return {
        ...profile.limits[periodType],
        // subtype: periodType,
        description: getLimitText(profile.limits[periodType]),
      };
    }

    const def = { ...noDataLimit };
    def.subtype = periodType;
    return def;
  };

  const getLimitError = (value: number) => {
    const min = window.config.depositSettings?.defaultLimits?.[currency]?.min ?? 0;
    const max = window.config.depositSettings.defaultLimits?.[currency]?.max ?? 0;

    return value < min ? ERRORS.MINIMUM_LIMIT : value > max ? ERRORS.MAXIMUM_LIMIT : false;
  };

  const onLimitButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = e.currentTarget ?? (e.currentTarget as HTMLButtonElement);
    // get data-value from button
    const value = el.getAttribute('data-value');

    // MainEventBus.emit('NOTIFICATION', {
    //   // title: 'Limita a fost modificată cu succes!',
    //   message: `You have been self-excluded for <br/><strong>${value}</strong>!`,
    //   type: 'success',
    // });

    if (value) {
      setState((v) => ({
        ...v,
        limitValue: Number(value),
        limitError: getLimitError(Number(value)),
      }));
    }
  };

  const getLimitButtonValues = () => {
    // @ts-ignore
    const presets = window.config.limits?.defaultPresets ?? [20, 30, 50, 100, 200, 300, 500];
    return presets.map((preset: any) => ({
      value: preset,
      valueRendered: getFormattedCurrencyAmount({
        amount: preset,
        currency: currency ?? window.config?.defaultCurrency ?? 'RON',
        locale: '',
        digits: 0,
      }),
      id: preset,
      onClickHandler: onLimitButtonClick,
      active: state.limitValue === preset,
    }));
  };

  const limits: any = [];

  const onRemoveLimitLegacy = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const limitType = e?.currentTarget?.dataset?.id ?? '';
    // console.log('onRemoveLimit', { limitType, profLim: profile.limits, e });

    // @ts-ignore
    if (
      limitType &&
      profile.limits &&
      profile.limits[limitType] != null &&
      profile.limits[limitType].limit_amount != null
    ) {
      // already has limit so remove it
      dispatch(setNewLimit(1, limitType, -1));
      setState((v) => ({
        ...v,
        currentStep: 'default',
        limitType: 0,
      }));
    } else {
      // set a limit
      setState((v) => ({
        ...v,
        currentStep: 'default',
        limitType: 0,
      }));
    }
  };

  const onRemoveLimit = (limitType: number) => {
    // @ts-ignore
    if (
      limitType &&
      profile.limits &&
      profile.limits[limitType] != null &&
      profile.limits[limitType].limit_amount != null
    ) {
      // already has limit so remove it
      dispatch(setNewLimit(1, limitType, -1));
      setState((v) => ({
        ...v,
        currentStep: 'default',
        limitType: 0,
      }));
    } else {
      // set a limit
      setState((v) => ({
        ...v,
        currentStep: 'default',
        limitType: 0,
      }));
    }
  };

  const getLimits = () => {
    const cv = {
      lowLimitStr: '',
      lowLimitSeted: '',
      hasLimitDaily: false,
      hasLimitWeekly: false,
      hasLimitMonthly: false,
      hasLimitKYC: false,
    } as HashLimits;

    if (profile.limits && profile.limits['KYC'] != null && profile.limits['KYC'].limit_amount != null) {
      cv.hasLimitKYC = true;
      limits.push({ data: profile.limits['KYC'] });
    }

    if (profile.limits && profile.limits[3] != null && profile.limits[3].limit_amount != null) {
      cv.hasLimitMonthly = true;
      limits.push({ data: profile.limits[3] });
    }
    if (profile.limits && profile.limits[2] != null && profile.limits[2].limit_amount != null) {
      cv.hasLimitWeekly = true;
      limits.push({ data: profile.limits[2] });
    }

    if (profile.limits && profile.limits[1] != null && profile.limits[1].limit_amount != null) {
      cv.hasLimitDaily = true;
      limits.push({ data: profile.limits[1] });
    }

    if (limits.length > 0) {
      limits.sort((a: any, b: any) => a.data.limit_amount - b.data.limit_amount);
      const lowest = limits[0];
      cv.lowestLimit = limits[0];

      switch (lowest.data.period_type) {
        case 1:
          cv.lowLimitStr = 'Daily';
          cv.lowLimitSeted = limitTypes.daily.toString();
          break;
        case 2:
          cv.lowLimitStr = 'Weekly';
          cv.lowLimitSeted = limitTypes.weekly.toString();
          break;
        case 3:
          cv.lowLimitStr = 'Monthly';
          cv.lowLimitSeted = limitTypes.monthly.toString();
          break;
        case 'KYC':
          cv.lowLimitStr = 'KYC';
          cv.lowLimitSeted = limitTypes.KYC.toString();
          break;
        default:
          cv.lowLimitStr = '';
          cv.lowLimitSeted = '';
          break;
      }
    }

    return cv;
  };

  const contextValue = {
    ...state,
    limitDaily: { data: getLimit(limitTypes.daily) },
    limitWeekly: { data: getLimit(limitTypes.weekly) },
    limitMonthly: { data: getLimit(limitTypes.monthly) },
    limitKYC: { data: getLimit(limitTypes.KYC) },
    lowLimitStr: '',
    lowLimitSeted: '',
    lowestLimit: { data: getLimit(null) },

    smsAlert: marketing_agreements?.marketing_receive_sms === 1,
    emailAlert: marketing_agreements?.marketing_receive_email === 1,
    phoneAlert: marketing_agreements?.marketing_receive_phone === 1,
    partnersAlert: marketing_agreements?.marketing_partners === 1,

    currency: currency ?? window.config?.defaultCurrency ?? 'RON',
    limitButtonValues: getLimitButtonValues(),
    limitError: state.limitError,
    isDesktop,
    onSaveLimit: () => {
      if (
        !isNaN(Number(state.limitValue)) &&
        state.limitValue &&
        profile &&
        profile.limits &&
        // @ts-ignore
        profile.limits[state.limitType] != null &&
        // @ts-ignore
        // profile.limits[state.limitType].limit_amount != state.limitValue &&
        state.limitError === false
      ) {
        dispatch(setNewLimit(1, state.limitType, Number(state.limitValue)));
        setState((v) => ({
          ...v,
          currentStep: 'default',
          limitValue: initialState.limitValue,
        }));
      } else {
        setState((v) => ({
          ...v,
          currentStep: 'default',
          limitValue: initialState.limitValue,
        }));
        dispatch(requestLimits());
      }
    },
    onChangeLimit: (e: React.ChangeEvent<HTMLInputElement>) => {
      const el = e.currentTarget ?? (e.currentTarget as HTMLInputElement);
      const value = el.value;

      // regex to check if value is a positive number
      const regex = /^[0-9]*$/;
      if (!regex.test(value)) {
        return;
      }

      if (value.toString().length > 10) {
        return;
      }

      setState((v) => ({
        ...v,
        limitValue: Number(value),
        limitError: getLimitError(Number(value)),
      }));
    },
    onRemoveLimit,
    onRemoveLimitDaily: () => {
      onRemoveLimit(limitTypes.daily);
    },
    onRemoveLimitWeekly: () => {
      onRemoveLimit(limitTypes.weekly);
    },
    onRemoveLimitMonthly: () => {
      onRemoveLimit(limitTypes.monthly);
    },
    onShowLimitDailyValue: () => {
      setState((v) => ({
        ...v,
        currentStep: 'amount',
        limitType: limitTypes.daily,
        limitValue: initialState.limitValue,
        limitError: initialState.limitError,
      }));
    },
    onShowLimitWeeklyValue: () => {
      setState((v) => ({
        ...v,
        currentStep: 'amount',
        limitType: limitTypes.weekly,
        limitValue: initialState.limitValue,
        limitError: initialState.limitError,
      }));
    },
    onShowLimitMonthlyValue: () => {
      setState((v) => ({
        ...v,
        currentStep: 'amount',
        limitType: limitTypes.monthly,
        limitValue: initialState.limitValue,
        limitError: initialState.limitError,
      }));
    },
    onBackHandler: () => {
      setState((v) => ({
        ...v,
        currentStep: 'default',
        limitType: 0,
        limitDaily: { data: getLimit(limitTypes.daily) },
        limitWeekly: { data: getLimit(limitTypes.weekly) },
        limitMonthly: { data: getLimit(limitTypes.monthly) },
      }));
    },
    onToggleConfirmationDialog: () => {
      setState((v) => ({
        ...v,
        isConfirmationDialogOpen: !v.isConfirmationDialogOpen,
      }));
    },
    onRemoveLimitLegacy,
    limitsLoading: profile.loading,
    limitsLoaded: profile.limitsLoaded,
    ...getLimits(),
  };

  // console.log('DEBUG Limits', { contextValue, limits, loading: profile.loading, loaded: profile.limitsLoaded });

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Limits;
